import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UsersIcon, FileTextIcon, ClipboardListIcon, SettingsIcon, EyeIcon, 
         TrendingUpIcon, ShieldIcon, FolderIcon, ActivityIcon, MailIcon, PhoneIcon, MapPinIcon, AlertTriangleIcon } from 'lucide-react';
import { Card, CardHeader, CardContent } from '@etherisc/ui-kit';
import { PrimaryButton } from '../components/buttons';
import { useAuth } from '../hooks/useAuth';
import { usePermissions } from '../lib/casl/hooks';
import { dashboardService, type DashboardStatistics } from '../services/dashboardService';

function getRoleDisplayName(role: string): string {
  const roleNames = {
    'admin': 'Administrator',
    'system_operator': 'System Operator',
    'product_admin': 'Product Administrator',
    'reinsurer': 'Reinsurer',
    'user': 'User'
  };
  return roleNames[role as keyof typeof roleNames] || role;
}

export function DashboardPage() {
  const { user } = useAuth();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const primaryRole = user?.roles?.[0];
  const [statistics, setStatistics] = useState<DashboardStatistics | null>(null);
  const [loadingStats, setLoadingStats] = useState(true);

  const hasPermission = (permission: string) => can(permission.split(':')[0], permission.split(':')[1]);

  // Load dashboard statistics
  useEffect(() => {
    const loadStatistics = async () => {
      setLoadingStats(true);
      try {
        const stats = await dashboardService.getDashboardStatistics();
        setStatistics(stats);
      } catch (error) {
        console.error('Error loading dashboard statistics:', error);
      } finally {
        setLoadingStats(false);
      }
    };

    loadStatistics();
  }, []);

  // Stats data array with real values and navigation
  const stats = [
    {
      label: "Farmers",
      value: loadingStats ? "..." : (statistics?.farmers.total || 0).toLocaleString(),
      icon: <UsersIcon size={20} />,
      permission: "farmers:read",
      bgColor: "bg-blue-50",
      iconColor: "text-blue-600",
      onClick: () => navigate('/admin/farmers'),
    },
    {
      label: "Active Products", 
      value: loadingStats ? "..." : (statistics?.products.active || 0).toString(),
      icon: <FileTextIcon size={20} />,
      permission: "products:read",
      bgColor: "bg-green-50",
      iconColor: "text-green-600",
      onClick: () => navigate('/admin/products'),
    },
    {
      label: "Quotes",
      value: loadingStats ? "..." : (statistics?.quotes.total || 0).toString(),
      icon: <ClipboardListIcon size={20} />,
      permission: "quotes:read",
      bgColor: "bg-purple-50",
      iconColor: "text-purple-600",
      onClick: () => navigate('/admin/quotes'),
    },
    {
      label: "Policies",
      value: loadingStats ? "..." : (statistics?.policies.total || 0).toString(),
      icon: <ShieldIcon size={20} />,
      permission: "policies:read",
      bgColor: "bg-amber-50",
      iconColor: "text-amber-600",
      onClick: () => navigate('/admin/quotes?status=approved'),
    },
  ];

  // Filter stats based on permissions
  const visibleStats = stats.filter(stat => hasPermission(stat.permission));

  // Define quick action sections (2x2 grid)
  const quickActionSections = [
    {
      title: "User Management",
      icon: <UsersIcon size={20} />,
      permission: "users:create",
      actions: [
        {
          label: "Add New User",
          icon: <UsersIcon size={16} />,
          onClick: () => navigate('/admin/users/create'),
          permission: "users:create",
        },
        {
          label: "Roles & Permissions",
          icon: <ShieldIcon size={16} />,
          onClick: () => navigate('/admin/roles'),
          permission: "roles:read",
        },
        {
          label: "Create Organisation",
          icon: <FolderIcon size={16} />,
          onClick: () => navigate('/admin/organizations/create'),
          permission: "organizations:create",
        },
      ]
    },
    {
      title: "Product Management",
      icon: <FileTextIcon size={20} />,
      permission: "products:create",
      actions: [
        {
          label: "Create Product",
          icon: <FileTextIcon size={16} />,
          onClick: () => navigate('/admin/products/create'),
          permission: "products:create",
        },
        {
          label: "New Quote",
          icon: <ClipboardListIcon size={16} />,
          onClick: () => navigate('/admin/quotes/create'),
          permission: "quotes:create",
        },
        {
          label: "Quote Management",
          icon: <ClipboardListIcon size={16} />,
          onClick: () => navigate('/admin/quotes'),
          permission: "quotes:read",
        },
      ]
    },
    {
      title: "Claims Management",
      icon: <AlertTriangleIcon size={20} />,
      permission: "claims:approve",
      actions: [
        {
          label: "Review Claims",
          icon: <AlertTriangleIcon size={16} />,
          onClick: () => {},
          permission: "claims:approve",
          disabled: true,
          comingSoon: true,
        },
        {
          label: "Pending Approvals",
          icon: <ClipboardListIcon size={16} />,
          onClick: () => {},
          permission: "claims:approve",
          disabled: true,
          comingSoon: true,
        },
        {
          label: "Process Payouts",
          icon: <TrendingUpIcon size={16} />,
          onClick: () => {},
          permission: "claims:payout",
          disabled: true,
          comingSoon: true,
        },
      ]
    },
    {
      title: "System Administration",
      icon: <SettingsIcon size={20} />,
      permission: "system:configure",
      actions: [
        {
          label: "Health Dashboard",
          icon: <ActivityIcon size={16} />,
          onClick: () => window.open('/staticHealthDashboard.html', '_blank'),
          permission: "system:monitor",
        },
        {
          label: "Database Management",
          icon: <SettingsIcon size={16} />,
          onClick: () => navigate('/admin/database'),
          permission: "system:configure",
        },
      ]
    },
  ];

  // Filter sections based on permissions (only show if user has the section permission)
  const visibleQuickActionSections = quickActionSections.filter(section => hasPermission(section.permission));

  return (
    <div className="space-y-6">
      {/* Page Heading */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard Overview</h1>
        <p className="text-gray-600 mt-1">
          Your insurance management portal
        </p>
      </div>

      {/* Welcome Card - User and Organization Information */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <UsersIcon size={24} className="text-blue-600" />
            <div>
              <h3>Welcome, {user?.name}</h3>
              <p className="text-sm text-gray-600">Here's your account overview</p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* User Information */}
            <div className="space-y-3">
              <h4 className="font-semibold text-gray-900 mb-3">Your Profile</h4>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <ShieldIcon size={16} className="text-blue-600" />
                  <span className="font-medium">Role:</span>
                  <span className="text-gray-700">{getRoleDisplayName(primaryRole || '')}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <MailIcon size={16} className="text-blue-600" />
                  <span className="font-medium">Email:</span>
                  <span className="text-gray-700">{user?.email}</span>
                </div>
              </div>
            </div>

            {/* Organization Information */}
            <div className="space-y-3">
              <h4 className="font-semibold text-gray-900 mb-3">Your Organisation</h4>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <FolderIcon size={16} className="text-green-600" />
                  <span className="font-medium">Name:</span>
                  <span className="text-gray-700">{user?.organization?.name || 'N/A'}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <EyeIcon size={16} className="text-green-600" />
                  <span className="font-medium">Code:</span>
                  <span className="text-gray-700">{user?.organization?.code || 'N/A'}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <ShieldIcon size={16} className="text-green-600" />
                  <span className="font-medium">Type:</span>
                  <span className="text-gray-700">{user?.organization?.type || 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Organization Contact Details */}
            <div className="space-y-3">
              <h4 className="font-semibold text-gray-900 mb-3">Organization Contact</h4>
              <div className="space-y-2">
                {(user?.organization as unknown as { phone?: string })?.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <PhoneIcon size={16} className="text-purple-600" />
                    <span className="font-medium">Phone:</span>
                    <span className="text-gray-700">{(user?.organization as unknown as { phone?: string })?.phone}</span>
                  </div>
                )}
                {(user?.organization as unknown as { settings?: { email?: string } })?.settings?.email && (
                  <div className="flex items-center gap-2 text-sm">
                    <MailIcon size={16} className="text-purple-600" />
                    <span className="font-medium">Email:</span>
                    <span className="text-gray-700">{(user?.organization as unknown as { settings?: { email?: string } })?.settings?.email}</span>
                  </div>
                )}
                {user && ((user.organization as unknown as { addressLine1?: string })?.addressLine1 || 
                  (user.organization as unknown as { city?: string })?.city || 
                  (user.organization as unknown as { country?: string })?.country) && (
                  <div className="flex items-start gap-2 text-sm">
                    <MapPinIcon size={16} className="text-purple-600 mt-0.5" />
                    <div>
                      <span className="font-medium">Address:</span>
                      <div className="text-gray-700 mt-1">
                        {(user.organization as unknown as { addressLine1?: string })?.addressLine1 && (
                          <div>{(user.organization as unknown as { addressLine1?: string }).addressLine1}</div>
                        )}
                        {(user.organization as unknown as { addressLine2?: string })?.addressLine2 && (
                          <div>{(user.organization as unknown as { addressLine2?: string }).addressLine2}</div>
                        )}
                        {((user.organization as unknown as { city?: string })?.city || 
                          (user.organization as unknown as { stateProvince?: string })?.stateProvince || 
                          (user.organization as unknown as { postalCode?: string })?.postalCode) && (
                          <div>
                            {[(user.organization as unknown as { city?: string })?.city, 
                              (user.organization as unknown as { stateProvince?: string })?.stateProvince, 
                              (user.organization as unknown as { postalCode?: string })?.postalCode].filter(Boolean).join(', ')}
                          </div>
                        )}
                        {(user.organization as unknown as { country?: string })?.country && (
                          <div>{(user.organization as unknown as { country?: string }).country}</div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Stats Cards - Organization-specific KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {visibleStats.map((stat, index) => (
          <Card 
            key={index} 
            className={`${stat.bgColor} cursor-pointer hover:shadow-lg transition-shadow duration-200`}
            onClick={stat.onClick}
          >
            <CardHeader>
              <h3 className="text-sm font-medium text-gray-700">{stat.label}</h3>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="flex flex-col items-start">
                  <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                </div>
                <div className={`w-12 h-12 bg-white ${stat.iconColor} rounded-full flex items-center justify-center shadow-sm`}>
                  {stat.icon}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions - 2x2 Grid with Cards */}
      <div className="grid grid-cols-2 gap-6">
        {visibleQuickActionSections.map((section, sectionIndex) => (
          <Card key={sectionIndex}>
            <CardHeader>
              <div className="flex items-center gap-2">
                {section.icon}
                <h3>{section.title}</h3>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-3 max-w-md">
                {section.actions.map((action, actionIndex) => (
                  hasPermission(action.permission) && (
                    <PrimaryButton
                      key={actionIndex}
                      onClick={action.onClick}
                      disabled={'disabled' in action ? action.disabled : false}
                    >
                      {action.icon}
                      {action.label}
                      {'comingSoon' in action && action.comingSoon && ' (Coming Soon)'}
                    </PrimaryButton>
                  )
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
